import { useEffect, useMemo, useState } from 'react';

import { api, CareServiceOrgansKey, ReportResponse } from '@/api';
import { useIsDDFeatureFlagEnabled } from '@/core/data-dog';
import { FindingsByOrgan } from '@/shared/model';
import { categorizeFindings, groupFindingsByOrgan } from '@/shared/util';
import { useDefaultFindingStore, useFindingStore } from '@/store';

export function useUpdateFindingsStores(report: ReportResponse | undefined) {
  const isRqFindingsEnabled = useIsDDFeatureFlagEnabled('FCT_RQ_FINDINGS');
  const [hydratedForUuid, setHydratedForUuid] = useState<string | undefined>(undefined);

  const { data: renderedSummaries, isLoading: isQueryLoading } =
    api.fct.renderedSummary.query.useGetReportRenderedSummaries({
      payload: { uuid: report?.uuid },
    });

  useEffect(() => {
    if (isRqFindingsEnabled || renderedSummaries === undefined) {
      return;
    }

    if (renderedSummaries.length) {
      const groupedData = groupFindingsByOrgan(renderedSummaries);

      Object.keys(groupedData).forEach((organ) => {
        const { commonFindings, defaultFindings: defaultFindingsData } = categorizeFindings(
          groupedData[organ as CareServiceOrgansKey],
        );

        useFindingStore.setState((state) => ({
          findings: { ...state.findings, [organ]: commonFindings },
        }));

        useDefaultFindingStore.setState((state) => ({
          defaultFindings: { ...state.defaultFindings, [organ]: defaultFindingsData },
        }));
      });
    } else {
      useFindingStore.setState({ findings: {} as FindingsByOrgan });
      useDefaultFindingStore.setState({ defaultFindings: {} as FindingsByOrgan });
    }

    setHydratedForUuid(report?.uuid);
  }, [isRqFindingsEnabled, renderedSummaries, report?.uuid]);

  const isLoading = isRqFindingsEnabled
    ? isQueryLoading
    : isQueryLoading || (renderedSummaries !== undefined && hydratedForUuid !== report?.uuid);

  const value = useMemo(() => ({ isLoading }), [isLoading]);

  return value;
}
